import { FolderContent } from '@/api/generatedApi';
import { AssetNameWithExtension } from '@/components/AssetNameWithExtension';
import { Ellipsis } from '@/components/Ellipsis';
import { FolderIcon } from '@/components/FolderIcon';
import { Thumbnail } from '@/components/Thumbnail';
import { useAssetPolling } from '@/hooks/useDataPolling/useAssetPolling';
import { useTenants } from '@/services/tenants/useTenants';
import { useOptimisticFolderName } from '@/hooks/useOptimisticFolderName';
import { getAssetName } from '@/utils/assets';
import { Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { getFileExtension } from '@physna/supported-files';
import { Link } from 'react-router-dom';
import { ThumbnailPreview } from '../ThumbnailPreview';

const handleLinkClick = (event: React.MouseEvent) => {
  event.stopPropagation();
};

export const createComparePageRenderName = (onFolderClick: (folderId: string) => void) => {
  const ComparePageRenderName = (parameters: GridRenderCellParams<FolderContent>) => {
    const { getPath } = useTenants();
    const { data: asset } = useAssetPolling(parameters);
    const { setOptimisticFolder } = useOptimisticFolderName();

    const handleFolderClick = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (parameters.row.contentType === 'folder') {
        setOptimisticFolder(parameters.row.id, parameters.row.name);
      }
      onFolderClick(parameters.row.id);
    };

    return parameters.row.contentType === 'asset' ? (
      <Link
        to={getPath(`asset/${parameters.row.id}`)}
        style={{ textDecoration: 'none', color: 'inherit' }}
        onClick={handleLinkClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
          <ThumbnailPreview primaryAsset={parameters.row} placement='right' message={getAssetName(parameters.row)}>
            <Box sx={{ cursor: 'pointer' }}>
              <Thumbnail size={42} asset={parameters.row} noLink={true} />
            </Box>
          </ThumbnailPreview>
          <AssetNameWithExtension
            name={getAssetName(parameters.row)}
            path={parameters.row.path}
            extension={getFileExtension(parameters.row.path)}
            showAssemblyPill={asset.isAssembly}
            isMissingDependencies={asset.isMissingDependencies}
          />
        </Box>
      </Link>
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={handleFolderClick}
      >
        <FolderIcon />
        <Ellipsis text={parameters.row.name} />
      </Box>
    );
  };

  ComparePageRenderName.displayName = 'ComparePageRenderName';

  return ComparePageRenderName;
};
